import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLandlordStore, StaffRole, StaffStatus } from '@/stores/useLandlordStore';
import { Shield, Plus, UserX, Building, ChevronRight, Edit2, Trash2, Filter, Search } from 'lucide-react';
import { LoadingScreen } from '@/components/common/LoadingSpinner';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '@/contexts/ToastContext';

const AVAILABLE_ROLES: StaffRole[] = ['Property Manager', 'Maintenance Staff', 'Accountant', 'Security', 'Admin'];
const AVAILABLE_STATUSES: StaffStatus[] = ['Active', 'Inactive', 'Pending', 'Suspended'];

export const LandlordStaff: React.FC = () => {
    const navigate = useNavigate();
    const { properties, fetchProperties, staff, fetchStaff, deleteStaff, loading } = useLandlordStore();
    const { success, error: toastError } = useToast();
    
    const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');
    const [roleFilter, setRoleFilter] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        fetchProperties();
    }, []);

    useEffect(() => {
        if (properties.length > 0 && !selectedPropertyId) {
            setSelectedPropertyId(properties[0].id);
        }
    }, [properties, selectedPropertyId]);

    useEffect(() => {
        if (selectedPropertyId) {
            fetchStaff(selectedPropertyId);
        }
    }, [selectedPropertyId]);

    // Filter staff based on role, status, and search query
    const filteredStaff = staff.filter(member => {
        const matchesRole = !roleFilter || member.role === roleFilter;
        const matchesStatus = !statusFilter || member.status === statusFilter;
        const matchesSearch = !searchQuery || 
            member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            member.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (member.phone && member.phone.includes(searchQuery));
        return matchesRole && matchesStatus && matchesSearch;
    });

    const handleDeleteClick = (e: React.MouseEvent, staffId: string) => {
        e.stopPropagation();
        setDeleteConfirmId(staffId);
    };

    const handleConfirmDelete = async (staffId: string) => {
        setIsDeleting(true);
        try {
            await deleteStaff(staffId);
            success('Staff Removed', 'Staff member has been removed successfully.');
            if (selectedPropertyId) {
                fetchStaff(selectedPropertyId);
            }
        } catch (err: any) {
            toastError('Delete Failed', err.message || 'Failed to remove staff member.');
        } finally {
            setIsDeleting(false);
            setDeleteConfirmId(null);
        }
    };

    const handleCancelDelete = () => {
        setDeleteConfirmId(null);
    };

    const getStatusBadgeClasses = (status: string) => {
        switch (status) {
            case 'Active':
                return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
            case 'Inactive':
                return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
            case 'Pending':
                return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
            case 'Suspended':
                return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
            default:
                return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
        }
    };

    const getRoleBadgeClasses = (role: string) => {
        return 'flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg w-fit text-sm font-semibold ring-1 ring-blue-200/50 dark:ring-blue-800/50';
    };

    if (!selectedPropertyId && properties.length === 0) return <LoadingScreen />;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-light-text dark:text-dark-text dark:text-white">Staff Management</h1>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle mt-1">Manage personnel, roles, and access per property.</p>
                </div>
                <button
                    onClick={() => navigate('/landlord/staff/new')}
                    className="flex items-center justify-center gap-2 bg-primary text-white px-6 py-3 rounded-xl hover:bg-primary/90 transition-colors shadow-sm font-bold whitespace-nowrap"
                >
                    <Plus size={20} />
                    Invite Staff
                </button>
            </div>

            {/* Premium Filter Bar */}
            <div className="bg-light-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border space-y-4">
                {/* Property Selection */}
                <div className="relative w-full">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={18} />
                    <select
                        value={selectedPropertyId}
                        onChange={(e) => {
                            setSelectedPropertyId(e.target.value);
                            setRoleFilter('');
                            setStatusFilter('');
                            setSearchQuery('');
                        }}
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/50 text-light-text dark:text-dark-text font-medium appearance-none cursor-pointer"
                    >
                        <option value="" disabled>Select a property</option>
                        {properties.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                </div>

                {/* Filters Row */}
                <div className="flex flex-col md:flex-row gap-4 items-center">
                    {/* Search */}
                    <div className="relative w-full md:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={18} />
                        <input
                            type="text"
                            placeholder="Search staff..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg focus:outline-none focus:ring-2 focus:ring-primary/50 text-light-text dark:text-dark-text font-medium"
                        />
                    </div>

                    {/* Role Filter */}
                    <div className="relative w-full md:w-48">
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={16} />
                        <select
                            value={roleFilter}
                            onChange={(e) => setRoleFilter(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg focus:outline-none focus:ring-2 focus:ring-primary/50 text-light-text dark:text-dark-text font-medium appearance-none cursor-pointer"
                        >
                            <option value="">All Roles</option>
                            {AVAILABLE_ROLES.map(role => (
                                <option key={role} value={role}>{role}</option>
                            ))}
                        </select>
                    </div>

                    {/* Status Filter */}
                    <div className="relative w-full md:w-40">
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full pl-4 pr-4 py-2.5 rounded-lg border border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg focus:outline-none focus:ring-2 focus:ring-primary/50 text-light-text dark:text-dark-text font-medium appearance-none cursor-pointer"
                        >
                            <option value="">All Status</option>
                            {AVAILABLE_STATUSES.map(status => (
                                <option key={status} value={status}>{status}</option>
                            ))}
                        </select>
                    </div>

                    {/* Clear Filters */}
                    {(roleFilter || statusFilter || searchQuery) && (
                        <button
                            onClick={() => {
                                setRoleFilter('');
                                setStatusFilter('');
                                setSearchQuery('');
                            }}
                            className="px-4 py-2 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors whitespace-nowrap"
                        >
                            Clear Filters
                        </button>
                    )}
                </div>

                {/* Results Count */}
                <div className="text-sm text-light-subtle dark:text-dark-subtle">
                    Showing {filteredStaff.length} of {staff.length} staff member{staff.length !== 1 ? 's' : ''}
                </div>
            </div>

            {/* Staff Table */}
            <div className="bg-light-card dark:bg-dark-card rounded-xl shadow-sm border border-light-border dark:border-dark-border overflow-hidden">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-light-bg dark:bg-dark-bg dark:bg-gray-800/80">
                        <tr>
                            <th className="px-6 py-4 text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">Employee</th>
                            <th className="px-6 py-4 text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase tracking-wider hidden md:table-cell">Contact</th>
                            <th className="px-6 py-4 text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">Role</th>
                            <th className="px-6 py-4 text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase tracking-wider">Status</th>
                            <th className="px-6 py-4 text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase tracking-wider text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-light-border dark:divide-dark-border dark:divide-gray-800">
                        {loading && staff.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="px-6 py-12 text-center">
                                    <div className="flex flex-col items-center justify-center text-light-subtle dark:text-dark-subtle space-y-3">
                                        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                                        <p>Loading staff directory...</p>
                                    </div>
                                </td>
                            </tr>
                        ) : filteredStaff.length > 0 ? (
                            <AnimatePresence>
                                {filteredStaff.map((member, index) => (
                                    <motion.tr
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        transition={{ delay: index * 0.05 }}
                                        key={member.id}
                                        className="hover:bg-light-bg dark:bg-dark-bg transition-colors group"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-4">
                                                <div className="h-10 w-10 rounded-full bg-gradient-to-tr from-primary/20 to-primary/5 flex items-center justify-center text-primary font-bold shadow-sm ring-1 ring-primary/10">
                                                    {member.name.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-light-text dark:text-dark-text dark:text-white">{member.name}</p>
                                                    <p className="text-xs text-light-subtle dark:text-dark-subtle font-medium">ID: {member.id.substring(0, 8)}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 hidden md:table-cell">
                                            <span className="text-sm font-medium text-light-subtle dark:text-dark-subtle dark:text-gray-300">
                                                {member.phone || 'Not provided'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className={getRoleBadgeClasses(member.role)}>
                                                <Shield size={14} />
                                                {member.role}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 text-xs rounded-full font-bold uppercase tracking-wider ${getStatusBadgeClasses(member.status)}`}>
                                                {member.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                {deleteConfirmId === member.id ? (
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => handleConfirmDelete(member.id)}
                                                            disabled={isDeleting}
                                                            className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                                                        >
                                                            {isDeleting ? (
                                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                            ) : (
                                                                <Shield size={16} className="rotate-45" />
                                                            )}
                                                        </button>
                                                        <button
                                                            onClick={handleCancelDelete}
                                                            disabled={isDeleting}
                                                            className="p-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
                                                        >
                                                            <span className="font-bold text-sm">✕</span>
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/landlord/staff/${member.id}`);
                                                            }}
                                                            className="p-2 text-light-subtle dark:text-dark-subtle hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                                                            title="View Details"
                                                        >
                                                            <ChevronRight size={18} />
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/landlord/staff/${member.id}/edit`);
                                                            }}
                                                            className="p-2 text-light-subtle dark:text-dark-subtle hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                                            title="Edit Staff"
                                                        >
                                                            <Edit2 size={18} />
                                                        </button>
                                                        <button
                                                            onClick={(e) => handleDeleteClick(e, member.id)}
                                                            className="p-2 text-light-subtle dark:text-dark-subtle hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                            title="Delete Staff"
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </motion.tr>
                                ))}
                            </AnimatePresence>
                        ) : (
                            <tr>
                                <td colSpan={5} className="px-6 py-16 text-center">
                                    <div className="flex flex-col items-center justify-center text-light-subtle dark:text-dark-subtle bg-light-bg dark:bg-dark-bg rounded-xl p-8 max-w-sm mx-auto border border-dashed border-light-border dark:border-dark-border">
                                        <UserX size={32} className="mb-3" />
                                        <h3 className="text-base font-bold text-light-text dark:text-dark-text mb-1">
                                            {searchQuery || roleFilter || statusFilter ? 'No Matching Staff' : 'No Staff Assigned'}
                                        </h3>
                                        <p className="text-sm text-light-subtle dark:text-dark-subtle mb-4">
                                            {searchQuery || roleFilter || statusFilter 
                                                ? 'Try adjusting your filters to find staff members.'
                                                : 'You have not assigned any personnel to this property yet.'}
                                        </p>
                                        {!searchQuery && !roleFilter && !statusFilter && (
                                            <button
                                                onClick={() => navigate('/landlord/staff/new')}
                                                className="text-primary text-sm font-bold hover:underline"
                                            >
                                                Invite someone now
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};